<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { certifications } from '$lib/stores/selection';
  import {
    certificationCatalogue,
    certificationCatalogueFailed,
    loadCertificationCatalogue
  } from '$lib/stores/certifications';
  import GroupedPicker from '$lib/components/GroupedPicker.svelte';

  /** The assistant's variant: no switch, and room to breathe. See TechnologyFilter. */
  export let embedded = false;

  /** Called after every tick, so the wizard can re-ask the running total. */
  export let onChange: (() => void) | undefined = undefined;

  let search = '';

  $: box = embedded ? 'h-4 w-4' : 'h-3 w-3';
  $: checkbox = `${box} rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200`;

  /**
   * Switched on by the customer or by a selection arriving (a saved search,
   * the assistant), and off only by the customer, which clears it. The same
   * contract as TechnologyFilter, for the same reason: ticking writes nothing,
   * it reveals a list.
   */
  let enabled = false;
  $: if ($certifications.length > 0) enabled = true;

  function toggleEnabled() {
    enabled = !enabled;
    if (!enabled) {
      certifications.set([]);
      onChange?.();
    }
  }

  onMount(loadCertificationCatalogue);

  $: groups = ($certificationCatalogue?.groups ?? []).map((g) => ({
    key: g.key,
    label: g.label,
    items: g.certifications
  }));

  function pick(slug: string) {
    certifications.set(
      $certifications.includes(slug)
        ? $certifications.filter((s) => s !== slug)
        : [...$certifications, slug]
    );
    onChange?.();
  }
</script>

<!--
  The certification filters: what a company states on its website as certified,
  grouped the server's way. The picker is the one the technologies use, so the
  two explain "any in a group, all of the groups" the same way.
-->
<div class={embedded ? 'space-y-4' : enabled ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  {#if !embedded}
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input type="checkbox" checked={enabled} on:click={toggleEnabled} class="mr-2 {checkbox}" />
        <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.certifications.title')}
        </span>
      </label>
    </div>
  {/if}

  {#if !enabled && !embedded}
    <!-- Ticked open, or nothing. -->
  {:else if $certificationCatalogueFailed}
    <p class="text-xs text-gray-500">{$_('filters.certifications.unavailable')}</p>
  {:else if groups.length === 0}
    <p class="text-xs text-gray-400">{$_('filters.certifications.loading')}</p>
  {:else}
    <p class="mb-2 text-xs text-ink-500">{$_('filters.certifications.hint')}</p>
    <GroupedPicker
      {groups}
      selected={$certifications}
      onPick={pick}
      placeholder={$_('filters.certifications.search')}
      anyOf={$_('filters.certifications.anyOf')}
      {embedded}
      bind:search
    />
  {/if}
</div>
